import { CheckCircle, CircleDashed, Coffee, XCircle } from "@phosphor-icons/react";

// Shared session-status chip used by Today and Calendar (Day + Week)
// surfaces. Same palette across the app so a "Completed" / "Absent" /
// "In progress" pill reads identically wherever it appears.
//
// FP3-122 — Calendar / Today / Session History parity: when a
// session was logged with `session_outcome=provider_unavailable`
// (see LogSessionDialog / DocumentQuickSessionDialog), the chip
// swaps to the amber "Provider unavailable" pill so the surface
// matches Session History and never contradicts the recorded
// outcome. This is orthogonal to `provider_unavailable` (broad
// time-off from `/provider-unavailability`) which is handled by
// `UnavailableBadge`; both may coexist on rare edge cases.
export default function SessionStatusChip({ status, attendance, sessionOutcome, size = "sm" }) {
  const s = (status || "scheduled").toLowerCase();
  const att = (attendance || "").toLowerCase();
  const outcome = (sessionOutcome || "").toLowerCase();
  const sizing =
    size === "xs"
      ? "text-[9px] px-1 py-0"
      : "text-[10px] px-1.5 py-0.5";
  const iconSize = size === "xs" ? 9 : 10;
  if (outcome === "provider_unavailable") {
    return (
      <span
        data-testid="session-status-provider-unavailable"
        className={`inline-flex items-center gap-1 font-semibold rounded bg-[#FBF6EC] text-[#5A3E12] ring-1 ring-[#E7D6B4] ${sizing}`}
      >
        <Coffee size={iconSize} weight="fill" />
        Provider unavailable
      </span>
    );
  }
  if (s === "completed" && att === "absent") {
    return (
      <span
        data-testid="session-status-absent"
        className={`inline-flex items-center gap-1 font-semibold rounded bg-[#FBEFEC] text-[#B85C5C] ${sizing}`}
      >
        <XCircle size={iconSize} weight="fill" />
        Absent
      </span>
    );
  }
  if (s === "completed" && att === "unavailable") {
    return (
      <span
        data-testid="session-status-unavailable"
        className={`inline-flex items-center gap-1 font-semibold rounded bg-[#FDF8F3] text-[#9C6B2E] ${sizing}`}
      >
        <CircleDashed size={iconSize} weight="bold" />
        Unavailable
      </span>
    );
  }
  if (s === "completed") {
    return (
      <span
        data-testid="session-status-completed"
        className={`inline-flex items-center gap-1 font-semibold rounded bg-[#DDEAE0] text-[#1E5A3F] ring-1 ring-[#1E5A3F]/15 ${sizing}`}
      >
        <CheckCircle size={iconSize} weight="fill" />
        Completed
      </span>
    );
  }
  if (s === "in-progress" || s === "in_progress") {
    return (
      <span
        data-testid="session-status-in-progress"
        className={`inline-flex items-center gap-1 font-semibold rounded bg-[#FDF8F3] text-[#D9A05B] animate-pulse ${sizing}`}
      >
        <CircleDashed size={iconSize} weight="bold" />
        In progress
      </span>
    );
  }
  if (s === "missed" || s === "cancelled" || s === "canceled") {
    return (
      <span
        data-testid="session-status-missed"
        className={`inline-flex items-center gap-1 font-semibold rounded bg-[#FBEFEC] text-[#B85C5C] ${sizing}`}
      >
        <XCircle size={iconSize} weight="fill" />
        {s === "missed" ? "Missed" : "Cancelled"}
      </span>
    );
  }
  return null;
}
